import React, { Fragment, useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  DialogContentText,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import PropTypes from "prop-types";

import Backdrop from "Components/Common/Backdrop";
import Modal from "Components/Common/Modal";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

import {
  useGetBuildingSchedulesQuery,
  useLazyGetFireSafetyObservationNoticeQuery,
} from "apis/FireSafety/FireSafetyObservation";
import { getImageUrl } from "utils";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";

const FireSafetyViewObservationNotice = ({
  handleClose,
  applicationId,
  buildingName,
  t,
}) => {
  const {
    data: { data: buildingSchedulesData } = {},
    isLoading,
    isFetching,
  } = useGetBuildingSchedulesQuery(applicationId);
  const [
    getObservationNotice,
    { data: { data: observationNoticeData } = {}, isLoading: isNoticeLoading },
  ] = useLazyGetFireSafetyObservationNoticeQuery();

  const [documentToPreview, setDocumentToPreview] = useState(null);

  const [scheduleId, setScheduleId] = useState("");

  const handleToggle = (id) => {
    if (scheduleId === id) {
      setScheduleId("");
    } else {
      setScheduleId(id);
    }
  };

  useEffect(() => {
    if (buildingSchedulesData?.length === 1) {
      setScheduleId(buildingSchedulesData[0].scheduleId);
    }
  }, [buildingSchedulesData]);

  useEffect(() => {
    (async () => {
      try {
        if (scheduleId) {
          await getObservationNotice(scheduleId);
        }
      } catch (e) {}
    })();
  }, [scheduleId]);

  if (isLoading || isFetching) {
    return <Backdrop open />;
  }

  return (
    <>
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          {...documentToPreview}
        />
      )}

      <Modal
        isOpen
        handleClose={handleClose}
        maxWidth="lg"
        title={t("Observation Notice") + " - " + buildingName}
      >
        {buildingSchedulesData?.length ? (
          buildingSchedulesData.map(
            ({ startDate, endDate, scheduleId: id }) => (
              <Fragment key={id}>
                <div
                  className="fs-4 fw-semibold mb-3 border-bottom"
                  onClick={() => handleToggle(id)}
                  key={id}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    className="cursor-pointer"
                  >
                    <div className="d-flex align-items-center gap-1">
                      <p className="fw-bold mb-0">{`${convertUTCDateToLocalDate(
                        startDate
                      ).format("DD-MM-YYYY")} - ${convertUTCDateToLocalDate(
                        endDate
                      ).format("DD-MM-YYYY")}`}</p>
                    </div>
                    <IconButton>
                      {scheduleId === id ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Stack>
                </div>
                <DialogContentText variant="subtitle2" component="div">
                  <Collapse in={scheduleId === id} timeout="auto" unmountOnExit>
                    {isNoticeLoading ? (
                      <div className="d-flex justify-content-center">
                        <CircularProgress size={30} />
                      </div>
                    ) : (
                      <Grid container columnSpacing={1} rowSpacing={1}>
                        {observationNoticeData?.length ? (
                          observationNoticeData.map(({ url, tenantName }) => {
                            return (
                              <>
                                <Grid item xs={12} md={6} sm={8} lg={6}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      justifyContent: "space-between",
                                      mb: 1,
                                    }}
                                  >
                                    <Box>
                                      <h5 className="mt-1">{tenantName}</h5>
                                    </Box>
                                    <Box sx={{ marginRight: "30px" }}>
                                      <Button
                                        variant="outlined"
                                        onClick={() =>
                                          setDocumentToPreview({
                                            url: getImageUrl(url),
                                            documentTypeName: `${tenantName} Notice Document`,
                                            documentType: "pdf",
                                            documentName: `${tenantName} Notice`,
                                          })
                                        }
                                      >
                                        {t("View Notice")}
                                      </Button>
                                    </Box>
                                  </Box>
                                </Grid>
                              </>
                            );
                          })
                        ) : (
                          <Grid item xs={12}>
                            <div className="d-flex justify-content-center">
                              <h4>There is currently no notice available.</h4>
                            </div>
                          </Grid>
                        )}
                      </Grid>
                    )}
                  </Collapse>
                </DialogContentText>
              </Fragment>
            )
          )
        ) : (
          <Typography variant="h6" className="text-center mt-3">
            {t("There is no observations added for this building till now")}
          </Typography>
        )}
      </Modal>
    </>
  );
};

FireSafetyViewObservationNotice.propTypes = {
  handleClose: PropTypes.func,
  applicationId: PropTypes.number,
  buildingName: PropTypes.string,
  t: PropTypes.func,
};

export default withTranslation()(FireSafetyViewObservationNotice);
